import {
  formatAbsenceReasonLabel,
  getAbsenceNoteText,
  getAbsenceTypeLabel,
  getApprovedAbsenceChipLabel,
  getApprovedAbsenceDisplayLabel,
  isApprovedVacationAbsence,
  isManualDayOffAbsence,
} from '../constants/absenceReasons';

export const ATTENDANCE_STATUS_LABELS: Record<string, string> = {
  EXPECTED: 'Expected',
  PRESENT: 'Present',
  SLIGHTLY_LATE: 'Slightly Late',
  LATE: 'Late',
  ABSENT: 'Absent',
  NO_CALL_NO_SHOW: 'no call/no show',
  SCHEDULED_OFF: 'Scheduled Off',
};

export const MISSING_AGENT_STATUS_LABELS: Record<string, string> = {
  expected: 'Expected',
  slightly_late: 'Slightly Late',
  late: 'Late',
  no_show: 'no call/no show',
  no_show_auto: 'no call/no show',
  severely_late: 'no call/no show',
  absent_approved: 'Approved',
  on_training: 'On Training',
  not_scheduled: 'Not Scheduled',
  unassigned: 'Unassigned',
};

/** Light blue → orange → red progression for grace / late / no-show tags. */
export const TIMING_TAG_COLORS = {
  grace: '#64b5f6',
  slightlyLate: '#64b5f6',
  late: '#ef6c00',
  noShow: '#c62828',
} as const;

/** Soft teal — distinct from late/absence colors on Missing Agents. */
export const TRAINING_CHIP_STYLE = {
  bgcolor: '#b2dfdb',
  color: '#00695c',
} as const;

export type MissingAgentChipStyle = {
  bgcolor: string;
  color: string;
  border?: string;
};

/** Approved absence chips on Missing Agents (day off vs vacation). */
export const APPROVED_ABSENCE_CHIP_STYLES = {
  dayOff: { bgcolor: '#c8e6c9', color: '#2e7d32' },
  vacation: { bgcolor: '#e1bee7', color: '#6a1b9a' },
  personal: { bgcolor: '#ffcc80', color: '#e65100' },
  default: { bgcolor: '#546e7a', color: '#fff' },
} as const;

export const ATTENDANCE_STATUS_COLORS: Record<string, string> = {
  EXPECTED: '#1e88e5',
  PRESENT: '#2e7d32',
  SLIGHTLY_LATE: TIMING_TAG_COLORS.slightlyLate,
  LATE: TIMING_TAG_COLORS.late,
  ABSENT: TIMING_TAG_COLORS.noShow,
  NO_CALL_NO_SHOW: TIMING_TAG_COLORS.noShow,
  SCHEDULED_OFF: '#546e7a',
};

export const MISSING_AGENT_CHIP_COLORS: Record<
  string,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  expected: 'info',
  slightly_late: 'warning',
  late: 'warning',
  no_show: 'error',
  no_show_auto: 'error',
  severely_late: 'error',
  absent_approved: 'info',
  unassigned: 'warning',
};

/** Distinct chip colors for missing-agent status tags (align with timing legend). */
export const MISSING_AGENT_CHIP_STYLES: Record<string, MissingAgentChipStyle> = {
  expected: { bgcolor: '#1e88e5', color: '#fff' },
  slightly_late: { bgcolor: TIMING_TAG_COLORS.grace, color: '#fff' },
  late: { bgcolor: TIMING_TAG_COLORS.late, color: '#fff' },
  no_show: { bgcolor: TIMING_TAG_COLORS.noShow, color: '#fff' },
  no_show_auto: { bgcolor: TIMING_TAG_COLORS.noShow, color: '#fff' },
  severely_late: { bgcolor: TIMING_TAG_COLORS.noShow, color: '#fff' },
  absent_approved: { bgcolor: '#546e7a', color: '#fff' },
  on_training: TRAINING_CHIP_STYLE,
  not_scheduled: { bgcolor: '#eceff1', color: '#546e7a', border: '1px solid #cfd8dc' },
  unassigned: { bgcolor: TIMING_TAG_COLORS.late, color: '#fff' },
};

export const ATTENDANCE_TIMING_TAGS = [
  {
    key: 'slightly_late',
    label: 'Grace 10 min · Slightly Late',
    shortLabel: 'Slightly Late',
    color: TIMING_TAG_COLORS.grace,
    textColor: '#fff',
  },
  {
    key: 'late',
    label: 'Late 15 min',
    shortLabel: 'Late',
    color: TIMING_TAG_COLORS.late,
    textColor: '#fff',
  },
  {
    key: 'no_show',
    label: 'no call/no show 30 min',
    shortLabel: 'no call/no show',
    color: TIMING_TAG_COLORS.noShow,
    textColor: '#fff',
  },
  {
    key: 'on_training',
    label: 'On Training',
    shortLabel: 'Training',
    color: TRAINING_CHIP_STYLE.bgcolor,
    textColor: TRAINING_CHIP_STYLE.color,
  },
  {
    key: 'not_scheduled',
    label: 'Not Scheduled',
    shortLabel: 'Not Scheduled',
    color: '#eceff1',
    textColor: '#546e7a',
  },
] as const;

export type MissingAgentChipContext = Pick<
  MissingAgentRow,
  'status' | 'absenceType' | 'absenceReason' | 'trainingDays'
>;

function resolveApprovedAbsenceChipStyle(
  agent: MissingAgentChipContext,
): MissingAgentChipStyle {
  const input = { absenceType: agent.absenceType, reason: agent.absenceReason };
  if (isManualDayOffAbsence(input)) {
    return APPROVED_ABSENCE_CHIP_STYLES.dayOff;
  }
  if (agent.absenceType === 'personal' || agent.absenceReason === 'PERSONAL') {
    return APPROVED_ABSENCE_CHIP_STYLES.personal;
  }
  if (isApprovedVacationAbsence(input)) {
    return APPROVED_ABSENCE_CHIP_STYLES.vacation;
  }
  return APPROVED_ABSENCE_CHIP_STYLES.default;
}

export function getMissingAgentChipSx(agent: MissingAgentChipContext): MissingAgentChipStyle {
  if (agent.status === 'absent_approved') {
    return resolveApprovedAbsenceChipStyle(agent);
  }
  return MISSING_AGENT_CHIP_STYLES[agent.status] || APPROVED_ABSENCE_CHIP_STYLES.default;
}

export function hexColorToJsPdfRgb(hex: string): [number, number, number] {
  const normalized = hex.replace('#', '');
  const expanded = normalized.length === 3
    ? normalized.split('').map((char) => char + char).join('')
    : normalized;
  const value = Number.parseInt(expanded, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

/** Apply dashboard chip colors to the Status column in jsPDF autoTable. */
export function applyMissingAgentStatusPdfCellStyle(
  cell: { styles: Record<string, unknown> },
  agent: MissingAgentChipContext,
): void {
  const { bgcolor, color } = getMissingAgentChipSx(agent);
  cell.styles.fillColor = hexColorToJsPdfRgb(bgcolor);
  cell.styles.textColor = hexColorToJsPdfRgb(color);
  cell.styles.fontStyle = 'bold';
  cell.styles.halign = 'center';
}

export function getAttendanceStatusLabel(status: string): string {
  return ATTENDANCE_STATUS_LABELS[status] || status.replace(/_/g, ' ');
}

export function getMissingAgentStatusLabel(status: string): string {
  return MISSING_AGENT_STATUS_LABELS[status] || status.replace(/_/g, ' ');
}

/** Chip text for a missing-agent row (includes reason for supervisor-approved absences). */
export function getMissingAgentChipLabel(agent: MissingAgentRow): string {
  if (agent.status === 'absent_approved') {
    return `${getApprovedAbsenceChipLabel({
      absenceType: agent.absenceType,
      reason: agent.absenceReason,
    })} / Approved`;
  }
  if (agent.status === 'on_training') {
    const days = Number(agent.trainingDays) || 1;
    return days === 1 ? 'On Training · Day 1' : `On Training · Day ${days}`;
  }
  return getMissingAgentStatusLabel(agent.status);
}

export interface MissingAgentScheduleSummary {
  scheduleId: string;
  campaign: string;
  scheduledStartTime?: string | null;
  scheduledEndTime?: string | null;
}

export interface MissingAgentRow {
  id: string;
  employeeId?: string;
  name: string;
  email?: string;
  role?: string;
  department: string;
  campaign: string;
  campaigns?: string[];
  scheduleId?: string | null;
  scheduledStartTime?: string | null;
  scheduledEndTime?: string | null;
  scheduleSummaries?: MissingAgentScheduleSummary[];
  scheduleCount?: number;
  minutesLate?: number;
  status: string;
  absenceType?: string | null;
  absenceReason?: string | null;
  trainingDays?: number | null;
}

export function formatShiftTimeRange(
  startTime?: string | null,
  endTime?: string | null,
): string | null {
  if (!startTime || !endTime) return null;
  return `${startTime.slice(0, 5)}–${endTime.slice(0, 5)}`;
}

export function getMissingAgentShifts(agent: MissingAgentRow): MissingAgentScheduleSummary[] {
  if (agent.scheduleSummaries?.length) {
    return agent.scheduleSummaries;
  }
  if (agent.scheduleId && agent.scheduledStartTime) {
    return [{
      scheduleId: agent.scheduleId,
      campaign: agent.campaign,
      scheduledStartTime: agent.scheduledStartTime,
      scheduledEndTime: agent.scheduledEndTime,
    }];
  }
  return [];
}

/** Compact shift line shown under campaign in the missing-agents list. */
export function getMissingAgentShiftPreview(agent: MissingAgentRow): string | null {
  const shifts = getMissingAgentShifts(agent);
  if (shifts.length === 0) return null;
  if (shifts.length === 1) {
    return formatShiftTimeRange(shifts[0].scheduledStartTime, shifts[0].scheduledEndTime);
  }
  const previews = shifts
    .slice(0, 2)
    .map((shift) => formatShiftTimeRange(shift.scheduledStartTime, shift.scheduledEndTime))
    .filter((value): value is string => Boolean(value));
  const extra = shifts.length - previews.length;
  if (extra > 0) {
    previews.push(`+${extra} more`);
  }
  return previews.join(' · ');
}

export function getMissingAgentAbsenceNote(agent: MissingAgentRow): string | null {
  return getAbsenceNoteText(agent.absenceReason);
}

export function getMissingAgentSecondaryText(agent: MissingAgentRow): string {
  if (agent.status === 'on_training') {
    const days = Number(agent.trainingDays) || 1;
    return days === 1
      ? '1 consecutive day scheduled on Training'
      : `${days} consecutive days scheduled on Training`;
  }
  if (agent.status === 'absent_approved') {
    const note = getMissingAgentAbsenceNote(agent);
    if (note) return note;
    if (agent.absenceType || agent.absenceReason) {
      return getApprovedAbsenceDisplayLabel({
        absenceType: agent.absenceType,
        reason: agent.absenceReason,
      });
    }
    return 'Supervisor-approved absence';
  }
  if (agent.status === 'no_show' || agent.status === 'no_show_auto' || agent.status === 'severely_late') {
    const statusLabel = getMissingAgentStatusLabel(agent.status);
    const entryType = agent.absenceType
      ? getAbsenceTypeLabel(agent.absenceType)
      : statusLabel;
    return `${statusLabel} — ${entryType}`;
  }
  if (agent.status === 'not_scheduled') {
    return 'No shift scheduled today';
  }
  if (agent.status === 'unassigned') {
    return 'Unassigned (no schedule)';
  }
  if (agent.status === 'expected') {
    return 'Awaiting clock in';
  }
  if ((agent.scheduleCount || 0) > 1) {
    return `${agent.minutesLate || 0} min late — no clock in (${agent.scheduleCount} shifts today)`;
  }
  return `${agent.minutesLate || 0} min late — no clock in`;
}

export const ATTENDANCE_TIMING_SUMMARY =
  'Grace 10 min · Slightly Late · Late 15 min · no call/no show 30 min (auto unless supervisor approves absence)';
